import { useFormik } from 'formik'
import React from 'react'
import ModalComponent from '..'
import { deepCopy, stripSingleSectionFields } from '../../../helpers'
import {
  useCreateHomeSectionMutation,
  useUpdateHomeSectionMutation,
} from '../../../redux-toolkits/home-page/homePage.slice'
import {
  ContentType,
  CreateHomePageSection,
  SingleSection,
} from '../../../redux-toolkits/home-page/homePage.type'
import { CreateHomeSectionSchema } from '../../../validation/validate'
import { Button } from '../../button'
import Icon from '../../icon'
import { Checkbox } from '../../inputs/Checkbox'
import { TextInput } from '../../inputs/textInput'
import { TabNav } from '../../nav/tabNav'

const MAX_CONTENT = 2

const contents: {
  title: string
  icon: string
  value: ContentType
}[] = [
  {
    title: 'Products',
    icon: 'product-icon',
    value: 'PRODUCTS',
  },
  {
    title: 'Essentials',
    icon: 'list-star',
    value: 'ESSENTIALS',
  },
  {
    title: 'Good list',
    icon: 'scroll',
    value: 'GOOD_LISTS',
  },
  {
    title: 'Creators',
    icon: 'storefront',
    value: 'CREATORS',
  },
  {
    title: 'Explore',
    icon: 'good-hand',
    value: 'MANUAL_ITEM',
  },
]

export const AddHomeSectionModal: React.FC<{
  open: boolean
  setOpen: (cal: boolean) => void
  onCloseCallback?: () => void
  onSuccessCallback?: (section: SingleSection) => void
  mode?: 'create' | 'edit'
  section?: SingleSection
}> = ({
  open,
  setOpen,
  onCloseCallback = () => {},
  onSuccessCallback = () => {},
  mode = 'create',
  section,
}) => {
  const [createSection, { isLoading: creating }] =
    useCreateHomeSectionMutation()

  const [modifySection, { isLoading: editing }] = useUpdateHomeSectionMutation()

  const {
    values,
    errors,
    handleSubmit,
    handleChange,
    handleBlur,
    touched,
    setFieldValue,
    isValid,
    resetForm,
  } = useFormik({
    initialValues: {
      title: mode === 'edit' ? section?.title : '',
      description: mode === 'edit' ? section?.description : '',
      sectionType: mode === 'edit' ? section?.sectionType : 'MANUAL_CURATION',
      status: mode === 'edit' ? section?.status : 'DRAFT',
      contentTypes: mode === 'edit' ? section?.contentTypes : [],
    } as CreateHomePageSection,
    enableReinitialize: true,
    validationSchema: CreateHomeSectionSchema,
    onSubmit: async (values) => {
      if (mode === 'create') {
        const resp = await createSection({
          ...values,
        })
        if (resp?.data?.success) {
          onSuccessCallback(resp.data.data)
        }
      } else {
        if (section) {
          const sectionCopy: SingleSection = deepCopy(section)

          const payload = stripSingleSectionFields(sectionCopy)

          const resp = await modifySection({
            id: section?._id ?? '',
            payload: {
              ...payload,
              ...values,
              // Filter out irrelevant items when content type has changed
              items: payload.items?.filter((t) =>
                values.contentTypes.includes(t.type),
              ),
            },
          })

          if (resp?.data?.success) {
            onSuccessCallback(resp.data.data)
          }
        }
      }
    },
  })
  return (
    <ModalComponent
      open={open}
      setOpen={setOpen}
      onCloseCallback={() => {
        resetForm()
        onCloseCallback()
      }}
      title={mode === 'create' ? 'Create new section' : 'Edit section'}
      childrenClassName="md:!max-w-[420px]"
    >
      <form
        className="flex flex-col items-center justify-center gap-6"
        onSubmit={handleSubmit}
      >
        <TextInput
          // title="Section name"
          onChange={handleChange}
          onBlur={handleBlur}
          errors={touched.title && errors.title ? errors.title : ''}
          name="title"
          value={values.title}
          hasIcon={false}
          placeholder="Section title"
        />

        <TabNav
          activeTab={values.sectionType}
          setActiveTab={(tab) => {
            setFieldValue('sectionType', tab)
            if (tab === 'AUTOMATED_CURATION') {
              setFieldValue('contentTypes', ['PRODUCTS'])
            } else if (tab === 'CATEGORY') {
              setFieldValue('contentTypes', ['CATEGORIES'])
            } else if (tab === 'MARKETING_BANNER') {
              setFieldValue('contentTypes', ['HOMEPAGE_BANNER'])
            } else {
              setFieldValue('contentTypes', [])
            }
          }}
          tabs={[
            { name: 'Manual Curation', id: 'MANUAL_CURATION' },
            { name: 'Automated Curation', id: 'AUTOMATED_CURATION' },
            { name: 'Marketing Banner', id: 'MARKETING_BANNER' },
            mode === 'create'
              ? { name: 'Category', id: 'CATEGORY' }
              : undefined,
          ]}
          className=""
          type="switcher"
          size="regular"
        />

        <TextInput
          // title="Section description"
          onChange={handleChange}
          onBlur={handleBlur}
          errors={
            touched.description && errors.description ? errors.description : ''
          }
          name="description"
          value={values.description}
          hasIcon={false}
          placeholder="Section title"
          type="textarea"
          containerClassName="hidden"
        />

        <div className="w-full">
          <p className="text-black font-medium">What do you want to show?</p>

          {values.sectionType === 'MANUAL_CURATION' && (
            <p className="mt-4 text-sm">
              {values.contentTypes.length} of {MAX_CONTENT} selected
            </p>
          )}

          {values.sectionType === 'MANUAL_CURATION' && (
            <div className="flex flex-col gap-3 mt-4">
              {contents.map((c, idx) => {
                const isSelected = values.contentTypes.includes(c.value)
                return (
                  <Checkbox
                    key={`${idx}`}
                    className="w-full pb-3 last:pb-0 border-b last:border-b-0"
                    checkStatus={isSelected}
                    disabled={
                      values.contentTypes.length >= MAX_CONTENT && !isSelected
                    }
                    handleChange={(checked) => {
                      if (checked) {
                        setFieldValue('contentTypes', [
                          ...values.contentTypes,
                          c.value,
                        ])
                      } else {
                        setFieldValue(
                          'contentTypes',
                          values.contentTypes.filter((i) => i !== c.value),
                        )
                      }
                    }}
                    text={
                      <div className="flex items-center text-sec-black gap-2">
                        <Icon width={24} height={24} id={c.icon} />
                        <p>{c.title}</p>
                      </div>
                    }
                  />
                )
              })}
            </div>
          )}

          {values.sectionType === 'AUTOMATED_CURATION' && (
            <div className="flex flex-col gap-3 mt-4">
              {contents
                .filter((c) => c.value === 'PRODUCTS')
                .map((c, idx) => {
                  return (
                    <div
                      key={`${idx}`}
                      className="flex items-center text-sec-black gap-2"
                    >
                      <Icon width={24} height={24} id={c.icon} />
                      <p>{c.title}</p>
                    </div>
                  )
                })}
            </div>
          )}

          {values.sectionType === 'CATEGORY' && (
            <div className="mt-4 flex items-center text-sec-black gap-2">
              <Icon width={24} height={24} id={'product-icon'} />
              <p>Categories</p>
            </div>
          )}

          {values.sectionType === 'MARKETING_BANNER' && (
            <div className="mt-4 flex items-center text-sec-black gap-2">
              <Icon width={24} height={24} id={'product-icon'} />
              <p>Marketing Banner</p>
            </div>
          )}
        </div>

        <Button
          label="Continue"
          type="submit"
          className="mt-2 w-full"
          disabled={!isValid || creating || editing}
          loading={creating || editing}
          primary
        />
      </form>
    </ModalComponent>
  )
}
